import { DropdownOption } from 'components/final-form/dropdownUtils';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { Button, Header, RadioProps } from 'semantic-ui-react';
import styled from 'styled-components';
import DatePicker from 'components/final-form/DatePicker';
import { noop } from 'utils/functionUtils';
import { PaymentJournalData, PaymentJournalSearchRequest } from 'ts-types/api.types';
import Radio from 'components/final-form/Radio';
import CheckBox from 'components/final-form/CheckBox';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import { searchPaymentJournal } from 'services/paymentJournalServices';
import { PaymentJournalFormData } from 'components/billing/payment-journal/PaymentJournalView';
import Select from 'components/final-form/Select';
import Input from 'components/final-form/Input';
import { PaymentJournalPaymentType } from 'ts-types/api.enums';
import { composeValidators, mustBeNumber, required } from 'utils/validatorUtils';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledContainer = styled.div`

   flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  .ui.header.tiny {
      font-size: 1em !important;
    }
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 40px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchGroup = styled.div`
  flex: 0 1 auto;
  margin-left: 5rem;

  &.noMargin {
    margin-left: 0;
  }
`;

const SearchButtonContainer = styled.div`
  flex: 1 1 100%;
  margin-top: 1rem;
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;

const RadioBtnsContainer = styled.div`
  margin-bottom: 16px;

  .field {
    min-width: unset;
  }

  .field + .field {
    margin-left: 2.5rem;
  }
`;

interface PaymentJournalSearchForm extends PaymentJournalSearchRequest {
  issuedBy: boolean
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  onFilterBills: (paymentJournalData: PaymentJournalData) => void,
  onBillsLoadingChange: (treatmentsLoading: boolean) => void,
  setOutputFormData: (outputFormData: PaymentJournalFormData) => void,
  treaters: Array<DropdownOption>,
  setErrorMessage: (errorMessage?: string) => void
}

class PaymentJournalOptionsSegment extends Component<Props> {

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();
  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  constructor(props: Props) {
    super(props);

    const {radioMap} = this;
    const {getMessage} = props;

    const paymentTypeValues = Object.values(PaymentJournalPaymentType);

    const radioDefinitions = generateRadioButtonOptionsFromEnum(paymentTypeValues, "paymentType", getMessage);

    radioDefinitions.forEach((radioDefinition, index) => {
      radioMap.set(paymentTypeValues[index], radioDefinition);
    });
  };

  initialSearchValues = (): Partial<PaymentJournalSearchForm> => {

    const {currentUser} = this.props;

    const fromDate = moment().startOf('month').format("YYYY-MM-DD");
    const toDate = moment().endOf('month').format("YYYY-MM-DD");

    return {
      mandateId: currentUser ? currentUser.userConfig.mandateId : undefined,
      fromDate: fromDate,
      toDate: toDate,
      paymentType: PaymentJournalPaymentType.ALL,
      cashBox: 0,
      issuedBy: false
    }
  };

  handleChange = ({values}: FormState, form: FormApi) => {
    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    const issuedBy = values.issuedBy;

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }

    if (!issuedBy) {
      form.change("treaterId", undefined);
    }
  };

  searchBills = (values: any) => {

    const {
      onBillsLoadingChange,
      onFilterBills,
      setOutputFormData,
      setErrorMessage,
      getMessage
    } = this.props;

    this.setState({
      searchData: values
    });

    onBillsLoadingChange(true);

    searchPaymentJournal(values, this.cancelTokenSource)
        .then(response => {
          onFilterBills(response);

          const outputFormData: PaymentJournalFormData = {
            mandateId: values.mandateId,
            fromDate: values.fromDate,
            toDate: values.toDate,
            paymentType: values.paymentType,
            treaterId: values.treaterId ? values.treaterId : null,
            cashBox: values.cashBox,
            bills: response.bills
          }
          setOutputFormData(outputFormData);
        })
        .catch(() => {
          setErrorMessage(getMessage("error.general"));
        })
        .finally(() => {
          onBillsLoadingChange(false);
        })
  };

  onEnterKeyPressed = (e: any, values: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      this.searchBills(values)
    }
  };

  render(): React.ReactElement {
    return (
        <FinalForm
            onSubmit={() => noop}
            initialValues={this.initialSearchValues()}
            subscription={{values: true, pristine: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    const {radioMap, props} = this;
    const {treaters, getMessage} = props;

    const disabledBefore = values.fromDate && new Date(values.fromDate);
    const disabledTreaters = !values.issuedBy;

    const disableCashBox = values.paymentType !== PaymentJournalPaymentType.CASH;

    const allBills = radioMap.get(PaymentJournalPaymentType.ALL);
    const normalBills = radioMap.get(PaymentJournalPaymentType.NORMAL);
    const cashBills = radioMap.get(PaymentJournalPaymentType.CASH);

    const hasValidationErrors = form.getState().hasValidationErrors;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <Header>{getMessage("searchOptions")}</Header>
          </StyledContainer>
          <StyledContainer style={{marginTop: 10}}
                           onKeyDown={(e) => this.onEnterKeyPressed(e, values)}>
            <SearchOptionsContainer>
              <SearchGroup className="noMargin">
                <Header size="tiny">{getMessage("paymentJournal.timePeriod")}</Header>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromDate"
                      component={DatePicker}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toDate"
                      component={DatePicker}
                      disabledBefore={disabledBefore}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("paymentJournal.paymentType")}</Header>
                <RadioBtnsContainer>
                  <Field
                      name="paymentType"
                      component={Radio}
                      radioDefinition={allBills}
                  />
                  <Field
                      name="paymentType"
                      component={Radio}
                      radioDefinition={normalBills}
                  />
                  <Field
                      name="paymentType"
                      component={Radio}
                      radioDefinition={cashBills}
                  />
                </RadioBtnsContainer>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("paymentJournal.cashBox")}:
                  </StyledLabel>

                  <Field
                      name="cashBox"
                      component={Input}
                      disabled={disableCashBox}
                      validate={composeValidators(required, mustBeNumber)}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("medicament.selection")}</Header>
                <SearchField>
                  <Field
                      name="issuedBy"
                      component={CheckBox}
                      label={getMessage("paymentJournal.issuedBy")}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="treaterId"
                      component={Select}
                      options={treaters}
                      disabled={disabledTreaters}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchButtonContainer>
                <Button
                    type="button" primary compact
                    onClick={() => this.searchBills(values)}
                    disabled={hasValidationErrors}
                >
                  {getMessage("search")}
                </Button>
              </SearchButtonContainer>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </StyledForm>
    )
  };

}

export default withAuthContext(withMessages(PaymentJournalOptionsSegment));
